// FILTRE DU PROJET par cycle de vie — phase (création, discovery, delivery,
// run, refonte) et version. Il vit dans le menu de gauche, sous le nom du projet actif, et
// s'applique à TOUT le projet : études, insights, triangulation.
//
// Le filtre est une préférence de LECTURE, par navigateur et par projet :
// il ne modifie rien, et deux personnes peuvent regarder le même projet sous
// deux angles. Il est mémorisé (localStorage, avec repli silencieux).
import { savePreference } from '../legal/cookieConsent';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useClassify, useProjectVersions } from '../api/queries';
import type { Lifecycle, Phase } from '../api/types';
import { Button, Chip, chipClass, Select } from '../components/ui';
import './lifecycle.css';

// Dans l'ordre de vie d'un produit.
export const PHASE_LABELS: Record<Phase, string> = {
  creation: 'Création',
  discovery: 'Discovery',
  delivery: 'Delivery',
  run: 'Run',
  refonte: 'Refonte',
};
const PHASES = Object.keys(PHASE_LABELS) as Phase[];

/** `all` = pas de filtre ; `none` = seulement les objets non classés. */
type PhaseFilter = 'all' | 'none' | Phase;
type VersionFilter = 'all' | 'none' | string;

interface FilterValue {
  phase: PhaseFilter;
  version: VersionFilter;
  active: boolean;
  setPhase: (p: PhaseFilter) => void;
  setVersion: (v: VersionFilter) => void;
  reset: () => void;
  matches: (o: Partial<Lifecycle>) => boolean;
}

const FilterContext = createContext<FilterValue | null>(null);
const storageKey = (projectId: string) => `ro.filter.${projectId}`;

function readStored(projectId: string): { phase: PhaseFilter; version: VersionFilter } {
  try {
    const raw = localStorage.getItem(storageKey(projectId));
    if (raw) {
      const v = JSON.parse(raw) as { phase?: string; version?: string };
      const phase = v.phase === 'none' || PHASES.includes(v.phase as Phase) ? (v.phase as PhaseFilter) : 'all';
      return { phase, version: typeof v.version === 'string' && v.version ? v.version : 'all' };
    }
  } catch {
    /* stockage indisponible : pas de filtre */
  }
  return { phase: 'all', version: 'all' };
}

export function ProjectFilterProvider({ projectId, children }: { projectId: string; children: ReactNode }) {
  const [state, setState] = useState(() => readStored(projectId));
  // Un changement de projet recharge SON filtre : le filtre du projet A ne doit
  // pas masquer silencieusement le contenu du projet B.
  const [forProject, setForProject] = useState(projectId);
  if (forProject !== projectId) {
    setForProject(projectId);
    setState(readStored(projectId));
  }

  const persist = useCallback((next: { phase: PhaseFilter; version: VersionFilter }) => {
    setState(next);
    savePreference(storageKey(projectId), JSON.stringify(next));
  }, [projectId]);

  const value = useMemo<FilterValue>(() => ({
    ...state,
    active: state.phase !== 'all' || state.version !== 'all',
    setPhase: (phase) => persist({ ...state, phase }),
    setVersion: (version) => persist({ ...state, version }),
    reset: () => persist({ phase: 'all', version: 'all' }),
    matches: (o) => {
      const phaseOk = state.phase === 'all' || (state.phase === 'none' ? !o.phase : o.phase === state.phase);
      const versionOk = state.version === 'all' || (state.version === 'none' ? !o.version : o.version === state.version);
      return phaseOk && versionOk;
    },
  }), [state, persist]);

  return <FilterContext.Provider value={value}>{children}</FilterContext.Provider>;
}

/** Le filtre courant. Hors d'un projet (aucun fournisseur), tout passe. */
export function useProjectFilter(): FilterValue {
  return useContext(FilterContext) ?? {
    phase: 'all', version: 'all', active: false,
    setPhase: () => {}, setVersion: () => {}, reset: () => {}, matches: () => true,
  };
}

/** Le bloc « Filtre » du menu de gauche, sous le nom du projet actif. */
export function NavFilter({ projectId }: { projectId: string }) {
  const f = useProjectFilter();
  const versions = useProjectVersions(projectId);
  const known = versions.data?.versions ?? [];
  // Une version mémorisée qui n'existe plus reste proposée : sinon le sélecteur
  // afficherait « Toutes » alors que le filtre masque encore des objets.
  const options = f.version !== 'all' && f.version !== 'none' && !known.includes(f.version) ? [f.version, ...known] : known;

  return (
    <div className={`nav-filter${f.active ? ' nav-filter-on' : ''}`} role="group" aria-label="Filtrer le projet">
      <div className="nav-filter-head">
        <span className="t-mono">Filtre</span>
        {f.active ? (
          <Button variant="link" size="s" type="button" onClick={f.reset}>Tout afficher</Button>
        ) : null}
      </div>
      <label className="nav-filter-field">
        <span>Phase</span>
        <Select value={f.phase} onChange={(e) => f.setPhase(e.target.value as PhaseFilter)}>
          <option value="all">Toutes</option>
          {PHASES.map((p) => (
            <option key={p} value={p}>
              {PHASE_LABELS[p]}{versions.data?.phase === p ? ' (actuelle)' : ''}
            </option>
          ))}
          <option value="none">Non classé</option>
        </Select>
      </label>
      <label className="nav-filter-field">
        <span>Version</span>
        <Select value={f.version} onChange={(e) => f.setVersion(e.target.value)}>
          <option value="all">Toutes</option>
          {options.map((v) => (
            <option key={v} value={v}>
              v{v.replace(/^v/i, '')}{versions.data?.currentVersion === v ? ' (actuelle)' : ''}
            </option>
          ))}
          <option value="none">Sans version</option>
        </Select>
      </label>
    </div>
  );
}

/** Rappel en tête de liste : ce que le filtre masque. Rien d'affiché s'il est inactif. */
export function FilterNote({ shown, total }: { shown: number; total: number }) {
  const f = useProjectFilter();
  if (!f.active || total === shown) return null;
  const hidden = total - shown;
  return (
    <p className="filter-note" role="status">
      {hidden} élément{hidden > 1 ? 's' : ''} masqué{hidden > 1 ? 's' : ''} par le filtre du projet.
      <button type="button" onClick={f.reset}>Tout afficher</button>
    </p>
  );
}

/**
 * Pastille de classement d'une carte (« Discovery · v2.0 », « Non classé »).
 * Pour qui peut écrire, elle s'ouvre pour reclasser l'objet.
 */
export function LifecycleChip({
  projectId, kind, id, phase, version, canWrite,
}: {
  projectId: string; kind: 'study' | 'insight' | 'triangulation'; id: string;
  phase: Phase | null; version: string | null; canWrite: boolean;
}) {
  const classify = useClassify(projectId);
  const versions = useProjectVersions(projectId);
  const [open, setOpen] = useState(false);
  const [p, setP] = useState<Phase | ''>(phase ?? '');
  const [v, setV] = useState(version ?? '');

  const label = phase || version
    ? [phase ? PHASE_LABELS[phase] : null, version ? `v${version.replace(/^v/i, '')}` : null].filter(Boolean).join(' · ')
    : 'Non classé';

  if (!canWrite) {
    return <Chip state={phase || version ? undefined : 'vide'} className="life-chip">{label}</Chip>;
  }

  return (
    <span className="life-wrap">
      <button
        type="button"
        className={`${chipClass({ state: phase || version ? undefined : 'vide' })} life-chip`}
        aria-expanded={open}
        onClick={() => { setP(phase ?? ''); setV(version ?? ''); setOpen((o) => !o); }}
        title="Classer par phase et version"
      >
        {label}
      </button>
      {open ? (
        <span className="life-pop" role="dialog" aria-label="Classer par phase et version">
          <label>
            <span>Phase</span>
            <Select value={p} onChange={(e) => setP(e.target.value as Phase | '')}>
              <option value="">Non classé</option>
              {PHASES.map((x) => <option key={x} value={x}>{PHASE_LABELS[x]}</option>)}
            </Select>
          </label>
          <label>
            <span>Version</span>
            <input list={`versions-${id}`} value={v} onChange={(e) => setV(e.target.value)} placeholder="2.0" />
            <datalist id={`versions-${id}`}>
              {(versions.data?.versions ?? []).map((x) => <option key={x} value={x} />)}
            </datalist>
          </label>
          <span className="life-pop-actions">
            <button type="button" onClick={() => setOpen(false)}>Annuler</button>
            <button
              type="button"
              className="life-save"
              disabled={classify.isPending}
              onClick={() => classify.mutate(
                { kind, id, phase: p || null, version: v.trim() || null },
                { onSuccess: () => setOpen(false) },
              )}
            >
              Enregistrer
            </button>
          </span>
        </span>
      ) : null}
    </span>
  );
}
